import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { FormattedDate, FormattedNumber } from 'react-intl';
import Box from 'ui-box';
import { ITableProps, minorScale, Spinner, Table } from '../../npaper';
import { useStores, Sale, ISaleSnapsnotOut } from '../../stores';
import { IPaginationFilter } from '../../stores/utils/modelWithCRUD';
import { useFilterFetch } from '../../stores/utils/useFilterFetch';
import { ContributionMarginText } from '../products/PriceTexts';
import { CreditNotesBadge } from './CreditNotesSection';
import { SaleStatusTag } from './SaleStatusTag';
import { SaleThumbnail } from './SaleThumbnail';

export interface ISalesTableProps extends ITableProps {
  filter?: IPaginationFilter<ISaleSnapsnotOut>['filter'];
}

export const SalesTable: React.FC<ISalesTableProps> = observer(
  ({ filter, ...props }) => {
    const { saleStore } = useStores();
    const { items, isLoading, setFilter } = useFilterFetch<typeof Sale>(
      saleStore as any,
      { filter: filter }
    );

    useEffect(() => {
      setFilter({ filter: filter });
    }, [filter, setFilter]);

    return !items || isLoading ? (
      <Spinner size={16} />
    ) : items.length > 0 ? (
      <Table {...props}>
        <thead>
          <tr>
            <th className="sticky">Salg</th>
            <th>Status</th>
            <th>Oprettet</th>
            <th>Titel</th>
            <th className="right">Subtotal</th>
            <th className="right">DB</th>
          </tr>
        </thead>
        <tbody>
          {items.map((sale) => (
            <tr key={sale.id}>
              <td className="sticky font-mono">
                <SaleThumbnail sale={sale} marginRight={minorScale(1)} />
              </td>
              <td>
                <Box display="inline-flex" alignItems="center" height="20px">
                  <SaleStatusTag sale={sale} small />
                  {sale.credit_notes.length > 0 && (
                    <CreditNotesBadge sale={sale} />
                  )}
                </Box>
              </td>
              <td>
                <FormattedDate format="default" value={sale.created_at} />
              </td>
              <td>{sale.title}</td>
              <td className="right font-mono">
                <FormattedNumber
                  // eslint-disable-next-line react/style-prop-object
                  style="currency"
                  currency={sale.currency}
                  currencyDisplay="code"
                  value={sale.sub_total}
                />
              </td>
              <td className="right font-mono">
                <ContributionMarginText
                  contributionMargin={sale.contribution_margin}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    ) : (
      <Box is="p">Ingen salg fundet</Box>
    );
  }
);
